/** The Events API from the app (1.3.0): the list, one event with its RSVPs, the person search, and the presses. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { EventDetail, EventsResponse, PersonHit } from '@shared/events';
import { apiFetch } from '@/lib/api';

const eid = (id: string) => encodeURIComponent(id);

export function useEvents() {
  return useQuery({ queryKey: ['events'], queryFn: () => apiFetch<EventsResponse>('/api/events'), staleTime: 20_000, refetchOnWindowFocus: true });
}

export function useEvent(id: string | null) {
  return useQuery({ queryKey: ['event', id], queryFn: () => apiFetch<EventDetail>(`/api/events/e/${eid(id!)}`), enabled: !!id, staleTime: 10_000, refetchOnWindowFocus: true });
}

export function usePeople(eventId: string, q: string, staff = false) {
  const query = q.trim();
  return useQuery({
    queryKey: staff ? ['event-people', eventId, query, 'staff'] : ['event-people', eventId, query],
    queryFn: () => apiFetch<{ people: PersonHit[] }>(`/api/events/e/${eid(eventId)}/people?q=${encodeURIComponent(query)}${staff ? '&staff=1' : ''}`),
    enabled: query.length >= 1,
    staleTime: 5_000,
  });
}

/** 22 Oct 2026 (D-1022-NOLIMIT): the browse list behind "Add people" — every active tenant (and volunteer, when asked), A to Z by last name. */
export function useBrowsePeople(eventId: string, volunteers: boolean, enabled: boolean) {
  return useQuery({
    queryKey: ['event-people-all', eventId, volunteers],
    queryFn: () => apiFetch<{ people: PersonHit[] }>(`/api/events/e/${eid(eventId)}/people?all=1${volunteers ? '&volunteers=1' : ''}`),
    enabled,
    staleTime: 30_000,
  });
}

export const press = <T,>(eventId: string, body: Record<string, unknown>) => apiFetch<T>(`/api/events/e/${eid(eventId)}/press`, { method: 'POST', body: JSON.stringify(body) });
export const createEventReq = <T,>(body: unknown) => apiFetch<T>('/api/events/new', { method: 'POST', body: JSON.stringify(body) });

/** After any press: the event, the list (counts) and the person search refresh. */
export function useEventRefresh() {
  const qc = useQueryClient();
  return (eventId?: string) => {
    void qc.invalidateQueries({ queryKey: ['events'] });
    if (eventId) void qc.invalidateQueries({ queryKey: ['event', eventId] });
    void qc.invalidateQueries({ queryKey: ['event-people'] });
  };
}
